import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Play, ExternalLink } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getList } from "@/lib/api";
import { IMG } from "@/lib/images";
import { PageHeader } from "@/components/Editorial";

const CATS = ["Toutes", "médiumnité", "histoire", "ésotérisme", "deuil", "anecdote historique", "rêves", "réflexions", "conférences"];

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return null;
}

export default function Videos() {
  const [cat, setCat] = useState("Toutes");
  const { data: videos = [], isLoading } = useQuery({ queryKey: ["videos"], queryFn: () => getList("videos") });

  useSeo({
    title: "Vidéos",
    description: "Médiathèque vidéo de Dorothée Lancelot : médiumnité, histoire, ésotérisme, deuil, anecdotes historiques, rêves, réflexions et conférences.",
    path: "/videos",
  });

  const filtered = cat === "Toutes" ? videos : videos.filter((v) => v.category === cat);

  if (!isLoading && videos.length === 0) return <Navigate to="/" replace />;

  return (
    <div>
      <PageHeader eyebrow="Médiathèque" title="Vidéos" intro="Une sélection de contenus autour de l'histoire, de l'ésotérisme, de la médiumnité, du deuil et des rêves." image={IMG.gothic} />

      <section className="dl-container py-14">
        <div className="flex flex-wrap gap-2 mb-12">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              className={`px-4 py-2 text-xs tracking-wide uppercase border transition-colors ${cat === c ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-[#F3E9D2]/50 italic">Aucune vidéo dans cette catégorie.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((v) => {
              const embed = toEmbed(v.link);
              return (
                <div key={v.id} className="dl-card overflow-hidden" data-testid={`video-${v.id}`}>
                  {embed ? (
                    <div className="aspect-video"><iframe src={embed} title={v.title} className="w-full h-full" allowFullScreen /></div>
                  ) : v.thumbnail_url ? (
                    <a href={v.link || "#"} target="_blank" rel="noreferrer" className="block relative aspect-video">
                      <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30"><Play size={40} className="text-white" /></div>
                    </a>
                  ) : null}
                  <div className="p-5">
                    {v.category && <div className="dl-eyebrow" style={{ fontSize: "0.55rem" }}>{v.category}</div>}
                    <h3 className="serif text-xl text-[#F3E9D2] mt-1">{v.title}</h3>
                    {v.link && <a href={v.link} target="_blank" rel="noreferrer" className="dl-link text-sm mt-2 inline-flex items-center gap-1">Voir <ExternalLink size={12} /></a>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
